/**
 * Personal thread categories — the viewer's own SECTIONS (chat-thread
 * experience PR-5, 2026-09-09; built-ins made first-class 2026-09-09, Miles:
 * "there are default thread categories and they need to be able to be moved
 * in order or top to bottom and they need to be able to be renamed … when I
 * label it 'direct messages' it needs to fold into the 'direct messages'
 * default category").
 *
 * ONE ordered list per viewer, on `users/{uid}/chat_threads_meta/categories`
 * (`{ items: [{id, name, order}], collapsed: string[] }`): the viewer's custom
 * categories AND the three built-in sections — direct messages, group
 * threads, threads — which have FIXED ids, a default label in the viewer's
 * language, and a stored `name` only once renamed. A thread joins a section
 * through `category` on the viewer's own index doc (a custom id or a
 * built-in id); an unfiled thread falls into its natural built-in. Nobody
 * else sees any of it.
 *
 * Pure shapes + normalizers; no Firestore here.
 */
import type { ThreadDisplayKind } from '../services/groupThreads/threadDisplayTitle';

export type BuiltinSectionKind = 'direct' | 'group' | 'threads';
export type ThreadListLang = 'en' | 'es';

export interface ThreadCategory {
  id: string;
  /** The viewer's name; '' on a built-in that keeps its default label. */
  name: string;
  /** Display order, top to bottom (ascending). */
  order: number;
  /** Set on the three built-in sections (fixed ids, default labels). */
  builtin?: BuiltinSectionKind;
  /**
   * This section was ADOPTED from a built-in (2026-09-13) and the threads that
   * were sitting in it have not been re-filed under it yet.
   *
   * Renaming a default section makes it YOURS and it keeps its threads, while
   * a fresh section under the default name is re-established for new threads of
   * that kind (Miles, 2026-09-12). The threads live on the viewer's own index
   * docs, which this list cannot reach, so the host does that batched write and
   * clears this field when it is done. Durable on purpose: a reload mid-move
   * finishes the job instead of stranding the threads.
   */
  adoptedFrom?: BuiltinSectionKind;
}

export const THREAD_CATEGORY_NAME_MAX = 40;
/** Custom categories only — the built-ins never count. */
export const MAX_THREAD_CATEGORIES = 24;

export const BUILTIN_SECTION_IDS: Record<BuiltinSectionKind, string> = {
  direct: 'direct-messages',
  group: 'group-threads',
  threads: 'threads',
};
/**
 * The DEFAULT top-to-bottom order of the built-ins (Miles, 2026-09-09: "the
 * default order of the categories should be: routines, group threads,
 * threads (… one-on-ones with Prest0n), then direct messages" — routines are
 * the rail's own section above the model). Custom categories sit above the
 * built-ins; a viewer who has REORDERED keeps their own order
 * (`builtinOrderTouched` on the meta doc), everyone else sees this.
 */
export const BUILTIN_SECTION_ORDER: readonly BuiltinSectionKind[] = ['group', 'threads', 'direct'];

export const DEFAULT_SECTION_LABELS: Record<ThreadListLang, Record<BuiltinSectionKind, string>> = {
  en: { direct: 'direct messages', group: 'group threads', threads: 'threads' },
  es: { direct: 'mensajes directos', group: 'hilos de grupo', threads: 'hilos' },
};

function trim(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** The built-in kind an id names, or null for a custom id. */
export function builtinKindOf(id: unknown): BuiltinSectionKind | null {
  const value = trim(id);
  for (const kind of BUILTIN_SECTION_ORDER) if (BUILTIN_SECTION_IDS[kind] === value) return kind;
  return null;
}

export function isBuiltinCategoryId(id: unknown): boolean {
  return builtinKindOf(id) !== null;
}

/** The built-in section an UNFILED thread falls into, by its display kind. */
export function builtinIdForKind(kind: ThreadDisplayKind): string {
  if (kind === 'direct') return BUILTIN_SECTION_IDS.direct;
  if (kind === 'group') return BUILTIN_SECTION_IDS.group;
  return BUILTIN_SECTION_IDS.threads;
}

/** A category name as stored: trimmed, single-spaced, bounded, never empty. */
export function normalizeThreadCategoryName(raw: unknown): string {
  return trim(raw).replace(/\s+/g, ' ').slice(0, THREAD_CATEGORY_NAME_MAX);
}

/** Case- and accent-insensitive name key ("Direct Messages" ≡ "direct messages" ≡ "mensajes directos"'s own key). */
export function foldCategoryName(raw: unknown): string {
  return normalizeThreadCategoryName(raw).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** A fresh category id (short, uid-scoped by the doc it lives in). */
export function newThreadCategoryId(nowMs: number = Date.now()): string {
  return `cat_${nowMs.toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

/** The label a section wears for this viewer: their name, else the built-in's default. */
export function categoryLabel(cat: Pick<ThreadCategory, 'name' | 'builtin'>, lang: ThreadListLang = 'en'): string {
  const own = normalizeThreadCategoryName(cat.name);
  if (own) return own;
  return cat.builtin ? DEFAULT_SECTION_LABELS[lang][cat.builtin] : '';
}

/** Every name a built-in answers to: its default label in both languages. */
function builtinNameKeys(kind: BuiltinSectionKind): string[] {
  return (Object.keys(DEFAULT_SECTION_LABELS) as ThreadListLang[]).map((lang) => foldCategoryName(DEFAULT_SECTION_LABELS[lang][kind]));
}

/** The built-in a NAME folds into (either language's default label), or null. */
export function builtinKindForName(raw: unknown): BuiltinSectionKind | null {
  const key = foldCategoryName(raw);
  if (!key) return null;
  for (const kind of BUILTIN_SECTION_ORDER) if (builtinNameKeys(kind).includes(key)) return kind;
  return null;
}

/**
 * The section whose current name — or, for a built-in, default label in
 * either language — matches `name`, or null. This is the FOLD rule: creating
 * "direct messages" never makes a second section beside the real one.
 */
export function findCategoryByName(items: readonly ThreadCategory[] | null | undefined, raw: unknown): ThreadCategory | null {
  const key = foldCategoryName(raw);
  if (!key) return null;
  for (const cat of items ?? []) {
    if (cat.name && foldCategoryName(cat.name) === key) return cat;
    if (cat.builtin && builtinNameKeys(cat.builtin).includes(key)) return cat;
  }
  return null;
}

function reindex(items: ThreadCategory[]): ThreadCategory[] {
  return items.map((c, index) => ({ ...c, order: index }));
}

export interface ParsedThreadCategories {
  items: ThreadCategory[];
  /**
   * Custom ids that FOLD into a built-in (a stored custom category named like
   * one — "direct messages" beside the real one): the entry is dropped from
   * `items`; threads filed under the custom id read as filed under the
   * built-in until the host re-files them.
   */
  foldAliases: Record<string, string>;
}

/**
 * Parse the stored `items` list. Malformed entries are dropped, duplicate ids
 * keep the first, names are normalized, a built-in id keeps its identity (an
 * empty name = the default label), a custom entry named like a built-in
 * folds into it, custom entries are capped, and order is repaired to 0..n-1
 * in the stored order (ties by name so two devices agree).
 */
export function parseThreadCategories(raw: unknown): ParsedThreadCategories {
  const list = Array.isArray(raw) ? raw : [];
  const seen = new Set<string>();
  const out: ThreadCategory[] = [];
  const foldAliases: Record<string, string> = {};
  let customCount = 0;
  for (const entry of list) {
    if (!entry || typeof entry !== 'object') continue;
    const id = trim((entry as { id?: unknown }).id);
    if (!id || seen.has(id)) continue;
    const name = normalizeThreadCategoryName((entry as { name?: unknown }).name);
    const order = Number((entry as { order?: unknown }).order);
    const builtin = builtinKindOf(id);
    if (builtin) {
      seen.add(id);
      out.push({ id, name, order: Number.isFinite(order) ? order : out.length, builtin });
      continue;
    }
    if (!name) continue;
    const folds = builtinKindForName(name);
    if (folds) {
      foldAliases[id] = BUILTIN_SECTION_IDS[folds];
      seen.add(id);
      continue;
    }
    if (customCount >= MAX_THREAD_CATEGORIES) continue;
    seen.add(id);
    customCount += 1;
    const adoptedFrom = builtinKindOf(
      BUILTIN_SECTION_IDS[(entry as { adoptedFrom?: BuiltinSectionKind }).adoptedFrom as BuiltinSectionKind]
        ?? '',
    );
    out.push({
      id,
      name,
      order: Number.isFinite(order) ? order : out.length,
      // Only when set: Firestore rejects an explicit `undefined`.
      ...(adoptedFrom ? { adoptedFrom } : {}),
    });
  }
  out.sort((a, b) => a.order - b.order || (a.name || a.id).localeCompare(b.name || b.id) || (a.id < b.id ? -1 : 1));
  return { items: reindex(out), foldAliases };
}

/** The stored list, parsed (custom + any stored built-ins). See `withBuiltinSections` for the full rail. */
export function normalizeThreadCategories(raw: unknown): ThreadCategory[] {
  return parseThreadCategories(raw).items;
}

/**
 * The full ordered section list: every built-in exactly once — a missing one
 * is appended in default order (after the custom ones, so a viewer who never
 * touched categories sees custom → group threads → threads → direct
 * messages, the default order Miles asked for).
 */
export function withBuiltinSections(items: readonly ThreadCategory[] | null | undefined): ThreadCategory[] {
  const present = new Set((items ?? []).map((c) => c.id));
  const out: ThreadCategory[] = [...(items ?? [])];
  for (const kind of BUILTIN_SECTION_ORDER) {
    const id = BUILTIN_SECTION_IDS[kind];
    if (!present.has(id)) out.push({ id, name: '', order: out.length, builtin: kind });
  }
  return reindex(out);
}

/**
 * The DEFAULT arrangement of a full list: the custom categories in their
 * stored order, then the built-ins in `BUILTIN_SECTION_ORDER` — each keeping
 * its stored name. Applied while the viewer has never reordered
 * (`builtinOrderTouched` false on the meta doc): a rename or a collapse also
 * stores the full list, and without this rule that write would have frozen
 * whatever order the built-ins happened to have (the 2026-09-09 "it doesn't
 * seem like I can change the order" report — a viewer who had renamed a
 * section was pinned to the old direct → group → threads order for good).
 */
export function orderBuiltinsByDefault(items: readonly ThreadCategory[] | null | undefined): ThreadCategory[] {
  const full = withBuiltinSections(items);
  // Classified by ID, not by the `builtin` stamp — a row is a built-in by
  // its fixed id whatever shape it was stored in.
  const kindOf = (c: ThreadCategory): BuiltinSectionKind | null => builtinKindOf(c.id) ?? c.builtin ?? null;
  const custom = full.filter((c) => kindOf(c) === null);
  const builtins = BUILTIN_SECTION_ORDER
    .map((kind) => full.find((c) => kindOf(c) === kind))
    .filter((c): c is ThreadCategory => !!c);
  return reindex([...custom, ...builtins]);
}

/**
 * A built-in section that is wearing a name somebody gave it, and therefore
 * still owes an adoption (2026-09-13).
 *
 * These are the viewers who reported the bug: renaming "group threads" to
 * "triage" only ever set a label — the id never moved, so new group threads
 * kept arriving in the section now called "triage" and nothing anywhere said
 * they would. They are migrated on read, so the fix reaches a list that was
 * renamed before it shipped and not only the next rename.
 */
export function builtinsNeedingAdoption(
  items: readonly ThreadCategory[] | null | undefined,
): ThreadCategory[] {
  return withBuiltinSections(items).filter((c) => {
    const kind = builtinKindOf(c.id);
    if (!kind) return false;
    const own = normalizeThreadCategoryName(c.name);
    // A built-in renamed to its own default label in either language is not
    // wearing anybody's name — that is just the label.
    return Boolean(own) && builtinKindForName(own) !== kind;
  });
}

export interface AdoptBuiltinResult {
  items: ThreadCategory[];
  /** The id the section now has. The threads displayed in it move here. */
  customId: string;
}

/**
 * Renaming a DEFAULT section makes it the viewer's own (Miles, 2026-09-12:
 * the threads already in it stay with the new name, and a fresh section under
 * the default name is re-established for new threads of that kind).
 *
 * The re-establishing needs no work: `withBuiltinSections` guarantees all
 * three built-ins exist for every viewer, always — so this only has to mint the
 * custom section, stamp it `adoptedFrom` for the host's batched re-file, and
 * hand the built-in back its default label. The new section takes the position
 * `createCategory` gives a new one (just above the built-ins), which is the
 * closest place to where it sat.
 *
 * null when the rename is not an adoption at all: not a built-in, an empty
 * name, the built-in's own default label (the caller's back-to-default branch),
 * a name another section already answers to, or the custom cap reached.
 */
export function adoptBuiltinSection(
  items: readonly ThreadCategory[] | null | undefined,
  builtinId: string,
  rawName: string,
  newId: string = newThreadCategoryId(),
): AdoptBuiltinResult | null {
  const kind = builtinKindOf(builtinId);
  if (!kind) return null;
  const name = normalizeThreadCategoryName(rawName);
  if (!name || builtinKindForName(name) === kind) return null;

  const full = withBuiltinSections(items);
  const target = full.find((c) => c.id === builtinId);
  if (!target) return null;
  const clash = findCategoryByName(full, name);
  if (clash && clash.id !== builtinId) return null;
  if (customCategories(full).length >= MAX_THREAD_CATEGORIES) return null;

  const at = full.findIndex((c) => !!builtinKindOf(c.id));
  const adopted: ThreadCategory = { id: newId, name, order: at, adoptedFrom: kind };
  const rest = full.map((c) => (c.id === builtinId ? { ...c, name: '' } : c));
  const insertAt = at < 0 ? rest.length : at;
  return {
    items: reindex([...rest.slice(0, insertAt), adopted, ...rest.slice(insertAt)]),
    customId: newId,
  };
}

/** Drop the adoption stamp once the host has re-filed the section's threads. */
export function clearAdoptionStamp(
  items: readonly ThreadCategory[] | null | undefined,
  customId: string,
): ThreadCategory[] {
  return (items ?? []).map((c) => {
    if (c.id !== customId || !c.adoptedFrom) return c;
    const { adoptedFrom: _dropped, ...rest } = c;
    return rest;
  });
}

/** The category a thread is filed under (custom or built-in, aliases followed), or null. */
export function categoryOf(
  categories: readonly ThreadCategory[] | null | undefined,
  categoryId: unknown,
  foldAliases?: Readonly<Record<string, string>> | null,
): ThreadCategory | null {
  const raw = trim(categoryId);
  if (!raw) return null;
  const id = foldAliases?.[raw] ?? raw;
  return (categories ?? []).find((c) => c.id === id) ?? null;
}

/** Custom categories only (never a built-in). */
export function customCategories(items: readonly ThreadCategory[] | null | undefined): ThreadCategory[] {
  return (items ?? []).filter((c) => !c.builtin);
}
